import React, { useRef, useLayoutEffect, useCallback } from 'react';
import { ItemsWrapper, PillIndicator, UnderlineIndicator, SegmentedControllerRoot } from './SegmentedController.styles';
import { ToggleItem } from './components/ToggleItem/ToggleItem';
import { useSpring } from 'react-spring';
import { createDisplayName } from '../../common/utils/createDisplayName';
import type { SegmentedControllerProps } from './SegmentedController.types';

const SegmentedController: React.FC<SegmentedControllerProps> = ({ items, isMobile, width: rootWidth, tabTextProps, variant = 'pill' }) => {
  const tabsContainer = useRef<HTMLDivElement>(null);
  const pillIndicatorRef = useRef<HTMLSpanElement>(null);
  const underlineIndicatorRef = useRef<HTMLSpanElement>(null);
  const hasInitializedUnderline = useRef(false);
  const hasInitializedPill = useRef(false);

  const [{ left, width: tabWidth }, set] = useSpring(() => ({ width: 0, left: 0 }));

  const updateUnderlineIndicator = useCallback(() => {
    const indicator = underlineIndicatorRef.current;
    const container = tabsContainer.current;
    if (!indicator || !container) return;

    const children = Array.from(container.children) as HTMLElement[];
    const activeTab = children.find(child => child.classList.contains('active'));
    if (!activeTab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const offsetLeft = tabRect.left - containerRect.left;
    const width = tabRect.width;

    if (width === 0) return;

    if (!hasInitializedUnderline.current) {
      indicator.style.transition = 'none';
    }

    indicator.style.transform = `translateX(${offsetLeft}px)`;
    indicator.style.width = `${width}px`;

    if (!hasInitializedUnderline.current) {
      // Force reflow then re-enable transitions after first positioning
      void indicator.offsetHeight;
      indicator.style.transition = '';
      hasInitializedUnderline.current = true;
    }
  }, []);

  const updatePillIndicator = useCallback(() => {
    if (!pillIndicatorRef.current || !tabsContainer.current) return;
    const children = Array.from(tabsContainer.current.children) as HTMLElement[];
    const activeTab = children.find(child => child.classList.contains('active'));
    if (!activeTab) return;
    const { width } = activeTab.getBoundingClientRect();
    if (width === 0) return;
    // Snap to position on first measurement so the pill is visible on initial render
    // instead of animating from width: 0
    set({ left: activeTab.offsetLeft, width, immediate: !hasInitializedPill.current });
    hasInitializedPill.current = true;
  }, [set]);

  useLayoutEffect(() => {
    if (variant === 'underline') {
      updateUnderlineIndicator();
    } else {
      updatePillIndicator();
    }
  }, [items, isMobile, tabTextProps, rootWidth, variant, updateUnderlineIndicator, updatePillIndicator]);

  return (
    <SegmentedControllerRoot
      width={rootWidth}
      isMobile={isMobile}
      variant={variant}
    >
      <ItemsWrapper
        ref={tabsContainer}
        variant={variant}
      >
        {items.map(item => (
          <ToggleItem
            key={item.id ?? item.label}
            isMobile={isMobile}
            isDisabled={item.isDisabled}
            textProps={tabTextProps}
            variant={variant}
            {...item}
            onClick={e => {
              if (!item.isActive) {
                item.onClick?.(e);
              }
            }}
          />
        ))}
      </ItemsWrapper>
      {variant === 'underline' ? (
        <UnderlineIndicator ref={underlineIndicatorRef} />
      ) : (
        <PillIndicator
          ref={pillIndicatorRef}
          style={{ transform: left.interpolate(value => `translate3d(${value}px, -50%, 0px)`), willChange: 'transform', width: tabWidth }}
        />
      )}
    </SegmentedControllerRoot>
  );
};

SegmentedController.displayName = createDisplayName('SegmentedController');

export { SegmentedController };
